import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';
import { DashboardState } from './dashboard.reducer';
import {
  selectDashboardFeatureFlags,
  selectIsAdmin,
  selectIsDepartmentAdmin,
  selectIsDeveloper,
  selectIsOrganizationAdmin,
} from './dashboard.selectors';

@Injectable({ providedIn: 'root' })
export class IssuerPermissionsFacade {
  private readonly _store = inject(Store<DashboardState>);

  private readonly _hierarchyEnabled$ = this._store
    .select(selectDashboardFeatureFlags)
    .pipe(map((featureFlags) => !!featureFlags?.issuer_hierarchy));

  // FF off → `admin` (existing behaviour). FF on → account-wide admin.
  readonly isOrganizationAdmin$: Observable<boolean> = combineLatest([
    this._hierarchyEnabled$,
    this._store.select(selectIsAdmin),
    this._store.select(selectIsOrganizationAdmin),
  ]).pipe(
    map(([hierarchyEnabled, isAdmin, isOrganizationAdmin]) =>
      hierarchyEnabled ? isOrganizationAdmin : !!isAdmin,
    ),
  );

  // FF off → false. FF on → department-specific admin.
  readonly isDepartmentAdmin$: Observable<boolean> = combineLatest([
    this._hierarchyEnabled$,
    this._store.select(selectIsDepartmentAdmin),
  ]).pipe(map(([hierarchyEnabled, isDepartmentAdmin]) => hierarchyEnabled && isDepartmentAdmin));

  // FF off → false. FF on → developer access level.
  readonly isDeveloper$: Observable<boolean> = combineLatest([
    this._hierarchyEnabled$,
    this._store.select(selectIsDeveloper),
  ]).pipe(map(([hierarchyEnabled, isDeveloper]) => hierarchyEnabled && isDeveloper));

  readonly isOrganizationAdmin: Signal<boolean> = toSignal(this.isOrganizationAdmin$, {
    initialValue: false,
  });

  readonly isDepartmentAdmin: Signal<boolean> = toSignal(this.isDepartmentAdmin$, {
    initialValue: false,
  });

  readonly isDeveloper: Signal<boolean> = toSignal(this.isDeveloper$, { initialValue: false });
}
